import React, { useState, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import { observe } from 'mobx';
import {
  Form,
  Select,
  Row,
  Col,
  Input,
  Button,
  message,
  Spin,
  Typography,
  Collapse,
  Popconfirm,
  Tooltip,
  DatePicker,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { AppStore, ShopStore, UserStore, WorkOrderStore } from 'stores';
import ContactStore from 'stores/ContactStore';
import styled from 'styled-components';
import DealershipChooser from 'components/DealershipChooser';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import AddPartModal from 'containers/WorkOrder/modals/AddPartModal';
import PurchaseOrdersCostSummary from './PurchaseOrdersCostSummary';
import PurchaseOrderLineItem from './PurchaseOrderLineItem';
import moment from 'moment';

const { Option } = Select;
const { Text } = Typography;
const { Panel } = Collapse;

const PurchaseOrderStatusEnum = {
  open: 'Open',
  posted: 'Posted',
};

const ActionsWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const AddItemsActions = styled.div`
  display: flex;
`;

const FormWrapper = styled.div`
  form {
    padding-top: 20px;
  }

  .ant-form-item-label {
    line-height: 14px;

    label {
      font-size: 13px;
      font-weight: 400;
    }
  }
`;

const CostSummaryWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;

  .purchase-orders-cost-summary {
    max-width: 400px;
  }
`;

const PurchaseOrdersForm = ({
  childRef,
  onSelectVendor,
  onSelectCreatedBy,
  purchaseOrder,
  onSave,
  onCancel,
  savingWorkOrder,
  isNew,
  hideSelectFleet,
}) => {
  const [form] = Form.useForm();

  const [initialLoading, setInitialLoading] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [loadingSavingNewPart, setLoadingSavingNewPart] = useState(false);
  const [shopVendors, setShopVendors] = useState([]);
  const [shopUsers, setShopUsers] = useState([]);
  const [shopParts, setShopParts] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [tax, setTax] = useState({
    type: 'percentage',
    value: 0,
  });
  const [disableFields, setDisableFields] = useState(false);
  const [confirmationPopoverVisible, setConfirmationPopoverVisible] =
    useState(false);

  const CONTACT_FORM_MODAL_ID = 'contact_form_modal_id';
  const ADD_PART_MODAL_ID = 'add_part_modal_id';

  const updateFormValues = (purchaseOrderData) => {
    if (!purchaseOrderData) return;

    form.setFieldsValue({
      purchaseOrderNumber: purchaseOrderData.purchase_order_number || '',
      referenceNumber: purchaseOrderData.reference_number || '',
      createdBy: purchaseOrderData.created_by,
      vendor: purchaseOrderData.id_vendor,
      status: purchaseOrderData.status || 'open',
      invoice_date: purchaseOrderData.invoice_date
        ? moment(purchaseOrderData.invoice_date)
        : undefined,
    });

    if (purchaseOrderData.lineItems)
      setLineItems(purchaseOrderData.lineItems || []);
    if (purchaseOrderData.tax)
      setTax(purchaseOrderData.tax || { type: 'percentage', value: 0 });
    setDisableFields(purchaseOrderData.status === 'posted');
  };

  useImperativeHandle(childRef, () => ({
    setState: (newState) => {
      if (newState.lineItems) setLineItems(newState.lineItems);
      if (newState.tax) setTax(newState.tax);
      if (newState.disableFields !== undefined)
        setDisableFields(newState.disableFields);
    },
    getState: () => ({
      lineItems,
      tax,
      disableFields,
    }),
    refreshVendors,
    refreshUsers,
    refreshShopParts,
    form,
    updateFormValues,
  }));

  useEffect(() => {
    init();

    const shopChangeWatcher = observe(ShopStore.currentShop, 'id', () => {
      if (ShopStore.isAdmin()) return;
      return init();
    });

    return () => {
      shopChangeWatcher();
    };
  }, []);

  useEffect(() => {
    if (purchaseOrder && Object.keys(purchaseOrder).length > 0) {
      form.setFieldsValue({
        purchaseOrderNumber: purchaseOrder.purchase_order_number || '',
        referenceNumber: purchaseOrder.reference_number || '',
        createdBy: purchaseOrder.created_by,
        vendor: purchaseOrder.id_vendor,
        status: purchaseOrder.status || 'open',
        invoice_date: purchaseOrder.invoice_date
          ? moment(purchaseOrder.invoice_date)
          : undefined,
      });

      if (purchaseOrder.lineItems) setLineItems(purchaseOrder.lineItems || []);
      if (purchaseOrder.tax)
        setTax(purchaseOrder.tax || { type: 'percentage', value: 0 });
      setDisableFields(purchaseOrder.status === 'posted');
    }
  }, [purchaseOrder]);

  const init = async () => {
    try {
      setInitialLoading(true);
      await refreshVendors();
      await refreshUsers();
      await refreshShopParts();
    } catch {
      message.error('Error on fetching vendors and users', 3);
    } finally {
      setInitialLoading(false);
    }
  };

  const refreshVendors = async (shopId = ShopStore.currentShop.id) => {
    try {
      const contacts = await ContactStore.getShopContacts(shopId);
      setShopVendors(contacts || []);
    } catch (error) {
      AppStore.addError('Failed to load vendors: ' + error.message);
    }
  };

  const refreshUsers = async (shopId = ShopStore.currentShop.id) => {
    try {
      const users = await UserStore.fetchShopUsers(shopId);
      setShopUsers(users || []);
    } catch (error) {
      AppStore.addError('Failed to load users: ' + error.message);
    }
  };

  const refreshShopParts = async (shopId = ShopStore.currentShop.id) => {
    try {
      const parts = await WorkOrderStore.getShopParts(shopId, {
        include_deleted: true,
      });
      setShopParts(parts || []);
    } catch (error) {
      AppStore.addError('Failed to load parts: ' + error.message);
    }
  };

  const onSubmitForm = async () => {
    if (disableFields) {
      const entityName =
        ShopStore.currentShop.id === 374 ? 'Invoice' : 'Purchase Order';
      return message.error(entityName + ' is posted, you cannot edit it', 3);
    }

    try {
      const values = await form.validateFields();
      onSave({
        ...values,
        id_shop: ShopStore.currentShop.id,
        purchase_order_number: values.purchaseOrderNumber,
        reference_number: values.referenceNumber,
        created_by: values.createdBy,
        id_vendor: values.vendor,
        lineItems,
        tax,
      });
    } catch (error) {
      let friendlyMsg = '';
      if (error.errorFields?.length > 0) {
        friendlyMsg = error.errorFields[0].errors[0];
      } else {
        friendlyMsg = 'Error on saving Work Order';
      }
      message.error(friendlyMsg, 3);
    }
  };

  const onClickAddExistingPart = () => {
    setLineItems([
      ...lineItems,
      {
        type: 'part',
        entityId: 'new',
        quantity: 1,
        volume: '',
        volumeUnit: '',
        unitCost: 0,
        totalCost: 0,
      },
    ]);
  };

  const onClickAddService = () => {
    setLineItems([
      ...lineItems,
      {
        type: 'service',
        entityId: '',
        quantity: 1,
        volume: '',
        volumeUnit: '',
        unitCost: 0,
        totalCost: 0,
      },
    ]);
  };

  const onSubmitAddVendorModal = async (data) => {
    try {
      setSavingContact(true);
      const { data: contact } = await ContactStore.createShopContact({
        ...data,
        shopId: ShopStore.currentShop.id,
      });
      AppStore.addSuccess('Contact saved successfully');
      await refreshVendors();
      form.setFieldsValue({ vendor: contact.id });
      AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
    } catch (error) {
      AppStore.addError('Error to save contact: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  const onSubmitAddPartModal = async (data) => {
    try {
      setLoadingSavingNewPart(true);

      const {
        description,
        number,
        cost,
        addToInventory = false,
        volumeunit,
      } = data;
      const getName = () =>
        number ? `${number} - ${description}` : description;

      const payload = {
        id_shop: ShopStore.currentShop.id,
        name: getName(),
      };

      const newPart = await WorkOrderStore.createPartOnCustomEndpoint(payload);
      await WorkOrderStore.updatePartOnCustomEndpoint({
        id: newPart.id,
        description,
        number,
        cost,
        volumeunit: volumeunit || '',
        addToInventory,
      });

      message.success('Part saved successfully', 3);
      AppStore.openModals.set(ADD_PART_MODAL_ID, false);
      await refreshShopParts();

      setLineItems([
        ...lineItems,
        {
          type: 'part',
          entityId: newPart.id,
          quantity: 1,
          volume: '',
          volumeUnit: '',
          unitCost: cost,
          totalCost: cost,
        },
      ]);
    } catch (error) {
      let friendlyMsg =
        'Sorry, we had some problems to save the Part. Try again later';
      if (error?.message && JSON.parse(error.message)) {
        const errorObj = JSON.parse(error.message);
        friendlyMsg = isArray(errorObj.message)
          ? errorObj.message[0]
          : errorObj.message;
      }
      message.error(friendlyMsg, 3);
    } finally {
      setLoadingSavingNewPart(false);
    }
  };

  const getCreatedByOptions = () => {
    const options = shopUsers.map((user, index) => (
      <Option key={index} value={user.userId}>
        {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
      </Option>
    ));

    return options;
  };

  const getVendorOptions = () => {
    return shopVendors.map((vendor, index) => (
      <Option key={index} value={vendor.id}>
        {vendor.name}
      </Option>
    ));
  };

  const getPartOptions = () => {
    const onlyUniquePartsByNumber = shopParts.reduce((acc, part) => {
      if (
        !acc.find((p) => String(p.number).trim() === String(part.number).trim())
      ) {
        acc.push(part);
      }
      return acc;
    }, []);

    const parts = onlyUniquePartsByNumber.map((part, index) => {
      let displayText =
        `${part.number} ${part.description}`.trim() || part.name;
      if (displayText === 'null null') {
        displayText = part.name;
      }
      return (
        <Option key={index} value={part.id}>
          {displayText}
        </Option>
      );
    });

    return [
      <Option key="new" value="new">
        [Select Part]
      </Option>,
      ...parts,
    ];
  };

  const getServiceOptions = () => [];

  const statusOptions = Object.keys(PurchaseOrderStatusEnum)
    .map((key) => ({
      key,
      value: PurchaseOrderStatusEnum[key],
    }))
    .map((status) => (
      <Option key={status.key} value={status.key}>
        {status.value}
      </Option>
    ));

  if (initialLoading) {
    return (
      <LoadingWrapper>
        <Spin />
      </LoadingWrapper>
    );
  }

  const initialFormValues = {
    purchaseOrderNumber: '',
    referenceNumber: '',
    createdBy: undefined,
    vendor: undefined,
    status: 'open',
    invoice_date: undefined,
  };

  return (
    <FormWrapper>
      {hideSelectFleet ? null : <DealershipChooser />}

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmitForm}
        initialValues={initialFormValues}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              label={
                ShopStore.currentShop.id === 374
                  ? 'Invoice Number:'
                  : 'Purchase Order Number:'
              }
              name="purchaseOrderNumber"
              rules={[{ required: true, message: 'PO Number required!' }]}
            >
              <Input
                disabled={true}
                readOnly={true}
                prefix="#"
                allowClear={true}
                placeholder="000000"
                data-testid="purchase-order-number-input"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Reference number:" name="referenceNumber">
              <Input
                disabled={disableFields}
                readOnly={disableFields}
                prefix="#"
                allowClear={true}
                placeholder="000000"
                data-testid="reference-number-input"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Created by:"
              name="createdBy"
              rules={[{ required: true, message: 'Created by required!' }]}
            >
              <Select
                disabled={disableFields}
                readOnly={disableFields}
                placeholder="Select a User"
                optionFilterProp="children"
                data-testid="created-by-select"
              >
                {getCreatedByOptions()}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Form.Item
              label="Select Vendor:"
              name="vendor"
              style={{ marginBottom: 5 }}
            >
              <Select
                disabled={disableFields}
                readOnly={disableFields}
                placeholder="Search by Vendor Name"
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                data-testid="vendor-select"
              >
                {getVendorOptions()}
              </Select>
            </Form.Item>
            <Button
              disabled={disableFields}
              icon={<PlusOutlined />}
              onClick={() => {
                AppStore.openModals.set(CONTACT_FORM_MODAL_ID, true);
              }}
            >
              Add New Vendor
            </Button>
          </Col>
          <Col span={6}>
            <Form.Item label="Invoice Date:" name="invoice_date">
              <DatePicker
                style={{ width: '100%' }}
                disabled={disableFields}
                readOnly={disableFields}
                data-testid="invoice-date-picker"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Popconfirm
              placement="top"
              title={
                'Changing the status to Posted means you cannot edit this purchase order anymore.\r\nDo you wish to continue?'
              }
              visible={confirmationPopoverVisible}
              onConfirm={() => {
                form.setFieldsValue({ status: 'posted' });
                setConfirmationPopoverVisible(false);
              }}
              onCancel={() => {
                form.setFieldsValue({ status: 'open' });
                setConfirmationPopoverVisible(false);
              }}
              okText="Yes"
              cancelText="No"
            />
            <Form.Item
              label="Status:"
              name="status"
              rules={[{ required: true, message: 'Status required!' }]}
            >
              <Select
                disabled={disableFields}
                readOnly={disableFields}
                placeholder="Select a Status"
                optionFilterProp="children"
                onChange={(value) => {
                  if (value === 'posted') {
                    setConfirmationPopoverVisible(true);
                  }
                }}
                data-testid="status-select"
              >
                {statusOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Line items panel */}
        <Row gutter={16}>
          <Col span={24}>
            <Text strong>Line Items</Text>
          </Col>
          <Col span={24}>
            <Collapse defaultActiveKey={['1']}>
              <Panel
                header={
                  ShopStore.currentShop.id === 374
                    ? 'New Invoice Line 1'
                    : 'New Purchase Order Line 1'
                }
                key="1"
              >
                {lineItems.map((item, index) => (
                  <PurchaseOrderLineItem
                    key={index}
                    item={item}
                    partOptions={getPartOptions()}
                    serviceOptions={getServiceOptions()}
                    onRemoveItem={(item) => {
                      setLineItems(
                        lineItems.filter((lineItem) => lineItem !== item)
                      );
                    }}
                    onChange={(item, key, value) => {
                      setLineItems(
                        lineItems.map((lineItem) => {
                          if (lineItem === item) {
                            const lineItemToReturn = {
                              ...lineItem,
                              [key]: value,
                            };
                            if (key === 'entityId' && item.type === 'part') {
                              const part = shopParts.find(
                                (part) => part.id === value
                              );
                              if (part?.cost) {
                                lineItemToReturn.unitCost = parseFloat(
                                  part.cost
                                );
                              } else {
                                lineItemToReturn.unitCost = 0;
                              }
                              // set the meas unit based on part
                              lineItemToReturn.volumeUnit =
                                part?.volumeunit || '';
                            }
                            return lineItemToReturn;
                          }
                          return lineItem;
                        })
                      );
                    }}
                  />
                ))}
                <AddItemsActions>
                  <Button
                    disabled={disableFields}
                    onClick={onClickAddExistingPart}
                    icon={<PlusOutlined />}
                    type="link"
                    data-testid="add-existing-part-button"
                  >
                    Add Existing Part
                  </Button>
                  <Button
                    disabled={disableFields}
                    onClick={() => {
                      AppStore.openModals.set(ADD_PART_MODAL_ID, true);
                    }}
                    icon={<PlusOutlined />}
                    type="link"
                    data-testid="add-new-part-button"
                  >
                    Add New Part
                  </Button>
                  <Button
                    disabled={disableFields}
                    onClick={onClickAddService}
                    icon={<PlusOutlined />}
                    type="link"
                    data-testid="add-service-button"
                  >
                    Add Service
                  </Button>
                </AddItemsActions>
              </Panel>
            </Collapse>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <CostSummaryWrapper>
              <PurchaseOrdersCostSummary
                className="purchase-orders-cost-summary"
                tax={tax}
                lineItems={lineItems}
                childRef={() => {}}
                onChangeTaxValue={(value) => {
                  setTax({
                    ...tax,
                    value,
                  });
                }}
                onChangeTaxType={(type) => {
                  setTax({
                    ...tax,
                    type,
                  });
                }}
              />
            </CostSummaryWrapper>
          </Col>
        </Row>

        {/* Actions */}
        <Row gutter={16}>
          <Col span={24}>
            <ActionsWrapper>
              <Button
                onClick={onCancel}
                data-testid="purchase-order-cancel-button"
              >
                Cancel
              </Button>
              <Tooltip
                title={
                  disableFields
                    ? (ShopStore.currentShop.id === 374
                        ? 'Invoice'
                        : 'Purchase Order') + ' is posted, you cannot edit it'
                    : confirmationPopoverVisible
                      ? 'Changing the status to Posted means you cannot edit this purchase order anymore'
                      : ''
                }
              >
                <Button
                  disabled={disableFields}
                  type="primary"
                  htmlType="submit"
                  style={{ marginLeft: 8 }}
                  loading={savingWorkOrder}
                  data-testid="purchase-order-save-button"
                >
                  Save
                </Button>
              </Tooltip>
            </ActionsWrapper>
          </Col>
        </Row>
      </Form>

      <ContactFormModal
        modalId={CONTACT_FORM_MODAL_ID}
        saving={savingContact}
        title={'Add New Vendor'}
        defaultData={{}}
        onCancel={() => {
          AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
        }}
        onSubmit={onSubmitAddVendorModal}
      />

      <AddPartModal
        modalId={ADD_PART_MODAL_ID}
        loadingSavingNewPart={loadingSavingNewPart}
        onCancel={() => {
          AppStore.openModals.set(ADD_PART_MODAL_ID, false);
        }}
        onSubmit={onSubmitAddPartModal}
        shopParts={shopParts}
      />
    </FormWrapper>
  );
};

PurchaseOrdersForm.propTypes = {
  childRef: PropTypes.func,
  onSelectVendor: PropTypes.func,
  onSelectCreatedBy: PropTypes.func,
  purchaseOrder: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  savingWorkOrder: PropTypes.bool,
  isNew: PropTypes.bool,
  hideSelectFleet: PropTypes.bool,
};

PurchaseOrdersForm.defaultProps = {
  purchaseOrder: {},
  savingWorkOrder: false,
  isNew: false,
  hideSelectFleet: false,
};

export default PurchaseOrdersForm;
